import React from 'react';

import * as S from './styles';

/** The design's six status colours, plus `off` for a state that has ended. */
export type Tone = 'ok' | 'info' | 'warn' | 'danger' | 'purple' | 'neutral' | 'off';

type Shape = 'dot' | 'badge' | 'tag';

interface Entry {
  label: string;
  tone: Tone;
}

/**
 * Every enum the API returns, with the label and colour the design gives it.
 *
 * This is the whole point of the component: the panel had five different
 * status renderers and one of them — the ticket detail — was keyed '1'-'8'
 * against an API that returns NEW / IN_PROGRESS / CLOSED_BY_*, so status,
 * priority and urgency all rendered '-' on the product's most-read screen.
 *
 * Keys are matched case-insensitively, so a backend that changes the casing
 * of an enum does not silently blank a column.
 */
const MAPS: Record<string, { shape: Shape; entries: Record<string, Entry> }> = {
  ticketStatus: {
    shape: 'dot',
    entries: {
      NEW: { label: 'New', tone: 'info' },
      IN_PROGRESS: { label: 'In progress', tone: 'warn' },
      CLOSED_BY_ADMIN: { label: 'Closed by admin', tone: 'ok' },
      CLOSED_BY_USER: { label: 'Closed by user', tone: 'off' }
    }
  },

  /* Priority and urgency are the same three values and the same treatment. */
  ticketPriority: {
    shape: 'badge',
    entries: {
      LOW: { label: 'Low', tone: 'ok' },
      MEDIUM: { label: 'Medium', tone: 'warn' },
      HIGH: { label: 'High', tone: 'danger' }
    }
  },

  order: {
    shape: 'dot',
    entries: {
      waiting: { label: 'Waiting', tone: 'warn' },
      preparing: { label: 'Preparing', tone: 'info' },
      delivered: { label: 'Delivered', tone: 'ok' },
      cancelled_by_user: { label: 'Cancelled by user', tone: 'danger' },
      cancelled_by_teaboy: { label: 'Cancelled by tea boy', tone: 'danger' }
    }
  },

  reservation: {
    shape: 'dot',
    entries: {
      active: { label: 'Active', tone: 'ok' },
      completed: { label: 'Completed', tone: 'info' },
      canceled: { label: 'Canceled', tone: 'danger' }
    }
  },

  /* Why a person is on a reservation. The API sends these lower-case, and the
     detail screen rendered them raw — `organizer` in a pill, in the middle of
     a card of properly-cased names. The design gives the organizer the purple
     badge and everyone else the neutral one. */
  reservationRole: {
    shape: 'badge',
    entries: {
      organizer: { label: 'Organizer', tone: 'purple' },
      invited: { label: 'Invited', tone: 'neutral' },
      guest: { label: 'Guest', tone: 'neutral' }
    }
  },

  survey: {
    shape: 'dot',
    entries: {
      waiting: { label: 'Waiting', tone: 'off' },
      'in-progress': { label: 'In progress', tone: 'warn' },
      done: { label: 'Done', tone: 'ok' },
      cancelled: { label: 'Cancelled', tone: 'danger' },
      expired: { label: 'Expired', tone: 'off' }
    }
  },

  /* The contributor endpoint returns these already title-cased. */
  contributor: {
    shape: 'badge',
    entries: {
      Pending: { label: 'Pending', tone: 'warn' },
      Attending: { label: 'Attending', tone: 'ok' },
      Rejected: { label: 'Rejected', tone: 'danger' },
      Cancelled: { label: 'Cancelled', tone: 'neutral' }
    }
  },

  /* A role is a name, not a state — no colour, and never the raw enum.
     Note the irregular casing of cafeTeriaManager; it is the API's. */
  role: {
    shape: 'tag',
    entries: {
      sAdmin: { label: 'Super admin', tone: 'neutral' },
      admin: { label: 'Admin', tone: 'neutral' },
      cafeTeriaManager: { label: 'Cafeteria manager', tone: 'neutral' },
      marketingManager: { label: 'Marketing manager', tone: 'neutral' },
      supporter: { label: 'Supporter', tone: 'neutral' },
      reservationManager: { label: 'Reservation manager', tone: 'neutral' },
      user: { label: 'Employee', tone: 'neutral' },
      teaboy: { label: 'Tea boy', tone: 'neutral' }
    }
  },

  /* menuItem.status is a boolean on the wire; it is stringified before lookup. */
  menuItem: {
    shape: 'dot',
    entries: {
      true: { label: 'Active', tone: 'ok' },
      false: { label: 'Inactive', tone: 'off' }
    }
  },

  /*
   * Who a sent notification went to.
   *
   * The list rendered this as a disabled checkbox in a column headed "Send to
   * all" — a control the reader cannot operate, standing in for a fact, and
   * an unticked box that reads as "no" without saying what the yes would have
   * been. The two audiences are named instead.
   */
  notificationAudience: {
    shape: 'tag',
    entries: {
      all: { label: 'Everyone', tone: 'neutral' },
      targeted: { label: 'Selected', tone: 'neutral' }
    }
  },

  /*
   * Which way someone went through a gate.
   *
   * The out value on the wire is **`OU`**, not `OUT` — it arrives truncated
   * to two characters from the access-control system, which is why
   * `TransactionTypeEnum` in `common/enums` already keys the filter that way.
   * The cell printed the raw code, so a column of `IN` / `OU` said less than
   * it looks like it says. Both spellings are mapped, since either could turn
   * up if the source column is ever widened.
   *
   * Info and neutral rather than ok and danger: this is a direction, not a
   * verdict, and nobody leaving the building is in an error state.
   */
  attendance: {
    shape: 'badge',
    entries: {
      IN: { label: 'Entry', tone: 'info' },
      OU: { label: 'Exit', tone: 'neutral' },
      OUT: { label: 'Exit', tone: 'neutral' }
    }
  },

  /* Reservation confirmation — a second axis alongside reservation status. */
  confirmation: {
    shape: 'badge',
    entries: {
      true: { label: 'Confirmed', tone: 'ok' },
      false: { label: 'Unconfirmed', tone: 'neutral' }
    }
  }
};

export type StatusDomain = keyof typeof MAPS;

interface IProps {
  /** Which enum `value` belongs to. Picks the labels, the colours and the shape. */
  domain: StatusDomain;
  /** The raw value straight off the API. Booleans and numbers are fine. */
  value?: string | number | boolean | null;
  /** Overrides the shape the domain would normally use. */
  shape?: Shape;
  /** Rendered when `value` is absent. Defaults to nothing at all. */
  fallback?: React.ReactNode;
  className?: string;
  style?: object;
}

/** Turns an unmapped enum into something readable rather than showing it raw. */
const humanise = (raw: string): string => {
  const spaced = raw
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * One status chip for every enum in the product.
 *
 *   <StatusChip domain="ticketStatus" value={ticket.status} />
 *   <StatusChip domain="role" value={user.role} />
 *
 * An unmapped value is never rendered raw: it falls back to a neutral chip
 * with the enum humanised (CLOSED_BY_ADMIN -> "Closed by admin"), so a new
 * backend state degrades to something readable instead of leaking
 * `cafeTeriaManager` into a cell or blanking the column.
 */
const StatusChip: React.FC<IProps> = ({ domain, value, shape, fallback = null, className, style }) => {
  const map = MAPS[domain];

  if (value === null || value === undefined || value === '') return <>{fallback}</>;

  const raw = String(value);
  const entry =
    map.entries[raw] ??
    Object.entries(map.entries).find(([key]) => key.toLowerCase() === raw.toLowerCase())?.[1] ??
    ({ label: humanise(raw), tone: 'neutral' } as Entry);

  const cls = `${entry.tone} ${className || ''}`.trim();

  switch (shape || map.shape) {
    case 'badge':
      return (
        <S.Badge className={cls} style={style}>
          {entry.label}
        </S.Badge>
      );
    case 'tag':
      return (
        <S.Tag className={cls} style={style}>
          {entry.label}
        </S.Tag>
      );
    default:
      return (
        <S.Dot className={cls} style={style}>
          <i />
          {entry.label}
        </S.Dot>
      );
  }
};

export default StatusChip;
